'use client';

import {
  AlignJustify,
  ChevronDown,
  ShoppingCart,
} from 'lucide-react';
import Link from 'next/link';
import { useSession, signIn, signOut } from 'next-auth/react';
import { useState } from 'react';
import useSWR from 'swr';

import useCartService from '@/lib/hooks/useCartStore';
import { useRouter } from 'next/navigation';
import Image from 'next/image';

const Header = () => {
  const { data: session } = useSession();
  const { items, init } = useCartService();
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const toggleDrawer = () => setDrawerOpen((prev) => !prev);

  return (
    <>
      {/* Scrollable Authorized Seller Line */}
      <div className="bg-primary text-white py-2 overflow-hidden whitespace-nowrap">
        <div className="animate-scroll text-sm md:text-base">
          ⭐ We offer a wide range of automotive parts and accessories ... ⭐
          ⭐ Quality products at competitive prices ... ⭐
          ⭐ We are authorised dealers of Bosch, Halonix, Lucas, Sona Comstar, Valeo, Varroc ... ⭐
          ⭐ Contact us for exclusive deals and discounts ... ⭐
          ⭐ Customer satisfaction is our top priority ... ⭐
          ⭐ We provide expert advice and support for all your automotive needs ... ⭐
          ⭐ We are authorised dealers of Bosch, Halonix, Lucas, Sona Comstar, Valeo, Varroc ... ⭐
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-base-100 shadow">
        <div className="container mx-auto flex items-center justify-between px-4 py-2">
          {/* Mobile Menu Icon */}
          <button
            className="md:hidden p-2"
            onClick={toggleDrawer}
            aria-label="Open menu"
          >
            <AlignJustify className="w-6 h-6" />
          </button>

          {/* Logo & Title */}
          <div className="hidden md:block items-center space-x-8 h-16">
            <Link href="/" className="flex items-center">
              <Image
                src="/images/banner/logo.jpeg"
                alt="Jalaram Automotive Logo"
                width={150}
                height={150}
                layout="fixed"
                className="object-contain md:w-10 md:h-10"
                style={{ height: '4em', width: '4em' }}
              />
              <p className="ml-2 text-2xl md:text-4xl font-extrabold uppercase tracking-wide text-primary">
                Jalaram Automotive
              </p>
            </Link>
          </div>

          {/* Mobile Logo & Title */}
          <div className="md:hidden flex items-center space-x-2">
            <Link href="/" className="flex items-center">
              <Image
                src="/images/banner/logo.jpeg"
                alt="Jalaram Automotive Logo"
                width={50}
                height={50}
                layout="fixed"
                className="object-contain"
                style={{ height: '5em', width: '5em', borderRadius: '15%' }}
              />
            </Link>
          </div>

          {/* Right Side: Cart & User Account */}
          <div className="flex items-center space-x-4">
            <Link href="/cart" className="relative">
              <ShoppingCart />
              {items.length > 0 && (
                <span className="badge badge-primary absolute -right-2 -top-2">
                  {items.reduce((acc, item) => acc + item.qty, 0)}
                </span>
              )}
            </Link>

            {session?.user ? (
              <UserDropdown session={session} init={init} />
            ) : (
              <button className="btn btn-ghost" onClick={() => signIn()}>
                Sign In
              </button>
            )}
          </div>
        </div>

        {/* Bottom Navigation Bar */}
        <nav className="hidden md:flex bg-base-200 py-2 shadow-sm">
          <div className="container mx-auto flex items-center justify-center space-x-6">
            <Link href="/" className="relative hover:text-primary">
              <span className="cursor-pointer after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 after:ease-in-out hover:after:scale-x-100">
                Home
              </span>
            </Link>
            <Link href="/search" className="relative hover:text-primary">
              <span className="cursor-pointer after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 after:ease-in-out hover:after:scale-x-100">
                Catalog
              </span>
            </Link>
            <Link href="/about-us" className="relative hover:text-primary">
              <span className="cursor-pointer after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 after:ease-in-out hover:after:scale-x-100">
                About Us
              </span>
            </Link>
            <Link href="/contact-us" className="relative hover:text-primary">
              <span className="cursor-pointer after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 after:ease-in-out hover:after:scale-x-100">
                Contact Us
              </span>
            </Link>
            <Link href="/policies-certificates" className="relative hover:text-primary">
              <span className="cursor-pointer after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 after:ease-in-out hover:after:scale-x-100">
                Policies & Certificates
              </span>
            </Link>
            {/* Search Box - Hidden on Mobile */}
            <div className="hidden md:block">
              <SearchBox />
            </div>
          </div>
        </nav>

        {/* Mobile Search Bar */}
        <div className="md:hidden px-4 pb-3">
          <SearchBox />
        </div>

        {/* Left Sidebar for Mobile */}
        {isDrawerOpen && <LeftDrawer closeDrawer={toggleDrawer} />}
      </header>
    </>
  );
};

// Add the CSS for the scrolling animation
const styles = `
  @keyframes scroll {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }

  .animate-scroll {
    display: inline-block;
    white-space: nowrap;
    animation: scroll 20s linear infinite;
  }
`;


if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

// Inject the styles into the document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}


const LeftDrawer = ({ closeDrawer }: { closeDrawer: () => void }) => {
  const { data: categories, isLoading, error } = useSWR('/api/products/categories');
  
  return (
    <div className="fixed inset-0 z-40 bg-black/50" onClick={closeDrawer}>
      <div
        className="flex h-full w-64 flex-col bg-base-100 shadow-lg"
        onClick={(e) => e.stopPropagation()} // Prevent closing on inner clicks
      >
        <button className="btn btn-square btn-ghost m-2 self-end" onClick={closeDrawer}>
          ✕
        </button>
        <ul className="menu space-y-2 p-4">
          <li className="text-lg font-bold">Navigation</li>
            {['Home', 'About Us', 'Contact Us', 'Policies & Certificates'].map((item) => (
            <li key={item}>
              <Link href={`/${item.toLowerCase().replace(' ', '-')}`} onClick={closeDrawer}>
              {item}
              </Link>
            </li>
            ))}
            <li>
            <Link href="/search" onClick={closeDrawer}>
              Catalog
            </Link>
            </li>
          <li className="text-lg font-bold">Shop Categories</li>
          {isLoading ? <li>Loading...</li> : categories?.map((cat: any) => (
            <li key={cat}>
              <Link href={`/search?category=${cat}`} onClick={closeDrawer}>
                {cat}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const UserDropdown = ({ session, init }: { session: any; init: () => void }) => (
  <div className="dropdown dropdown-end">
    <label tabIndex={0} className="btn btn-ghost">
      Welcome {session.user.name} <ChevronDown />
    </label>
    <ul className="menu dropdown-content w-52 bg-base-100 shadow-lg">
      {session.user.isAdmin && (
        <li>
          <Link href="/admin/dashboard">Admin Dashboard</Link>
        </li>
      )}
      <li>
        <Link href="/order-history">Order History</Link>
      </li>
      <li>
        <Link href="/profile">Profile</Link>
      </li>
      <li>
        <button
          className="btn btn-error btn-sm w-full"
          onClick={() => {
            signOut({ callbackUrl: '/signin' });
            init();
          }}
        >
          Sign Out
        </button>
      </li>
    </ul>
  </div>
);

const SearchBox = () => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const router = useRouter();

  const { data: categories , error } = useSWR('/api/products/categories');
  console.log('data category'+categories);
  if (error) {
    console.error('Error fetching categories:', error);
  }
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    router.push(`/search?type=${category}&q=${query}`);
  };

  return (
    <form onSubmit={handleSubmit} className="flex w-full space-x-2">
      <select
        className="select select-bordered w-1/3"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
      >
        <option value="all">All</option>
        {categories?.map((cat : any) => (
          <option key={cat} value={cat}>
            {cat}
          </option>
        ))}
      </select>
      <input
        className="input input-bordered w-full"
        type="text"
        placeholder="Search..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <button className="btn btn-primary" type="submit">
        Search
      </button>
    </form>
  );
};

export default Header;
